import {
    App,
    Button,
    ButtonGroup,
    Content,
    dispatchCustomEvent,
    FICheck,
    FIX,
    Heading,
    Icon,
    If,
    Main,
    Modal,
    Notification,
    NotificationContent,
    NotificationIcon,
    SpinnerOverlay,
    Stack,
    Text,
    Toggle,
    useCustomEvent,
    useLocalStorage,
    useStorage,
    useWindowEvent,
    View,
} from '@fold-dev/core'
import { useContext, useEffect, useState } from 'react'
import ConfettiExplosion from 'react-confetti-explosion'
import { Outlet, useNavigate, useParams } from 'react-router-dom'
import { Dock, LoadingLine, Nav } from '../components'
import { IS_CORDOVA } from '../constants'
import { WebsocketContext, WebsocketProvider } from '../contexts/websocket.context'
import { ONESIGNAL_KEY, WEBSOCKET_HOST } from '../environment'
import { askPushNotificationPermission, subscribeUser, subscribeUserMobile } from '../helpers'
import { logger } from '../helpers/util'
import { useAuth } from '../hooks/auth.hook'
import { useAppStore } from '../store/app'
import { LogoNew } from '../logo'
import { TbConfetti } from 'react-icons/tb'

export const Welcome = ({ onDismiss }) => {
    return (
        <Modal
            isVisible
            onDismiss={onDismiss}
            width={400}
            height="fit-content">
            <View
                column
                p="3rem 2rem"
                gap="2rem"
                colorToken="text">
                <Icon
                    style={{ width: 60, height: 60 }}
                    icon={TbConfetti}
                />
                <Heading
                    as="h2"
                    textAlign="center">
                    Welcome to Fronteer
                </Heading>
                <Text
                    size="lg"
                    lineHeight={1.3}
                    textAlign="center">
                    You're using the Early Access version of Fronteer. We're still polishing things up, so expect a few
                    rough edges. Your feedback helps shape what comes next — thanks for being part of the journey.
                </Text>
                <Text textAlign="center">
                    Please report any bugs or issues to{' '}
                    <a
                        href="mailto:support@fronteer.app"
                        style={{ color: 'inherit' }}>
                        support@fronteer.app
                    </a>
                </Text>
                <Button
                    variant="accent"
                    size="lg"
                    onClick={onDismiss}>
                    Get Started
                </Button>
            </View>
        </Modal>
    )
}

const Loader = (props) => (
    <svg
        width={30}
        height="100%"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid"
        style={{ position: 'relative', top: 3 }}>
        <g transform="translate(20 50)">
            <circle
                cx={0}
                cy={0}
                r={7}
                fill="var(--f-color-highlight)"
                transform="scale(0.99275 0.99275)">
                <animateTransform
                    attributeName="transform"
                    type="scale"
                    begin="-0.375s"
                    calcMode="spline"
                    keySplines="0.3 0 0.7 1;0.3 0 0.7 1"
                    values="0;1;0"
                    keyTimes="0;0.5;1"
                    dur="1s"
                    repeatCount="indefinite"
                />
            </circle>
        </g>
        <g transform="translate(40 50)">
            <circle
                cx={0}
                cy={0}
                r={7}
                fill="var(--f-color-highlight)"
                transform="scale(0.773605 0.773605)">
                <animateTransform
                    attributeName="transform"
                    type="scale"
                    begin="-0.25s"
                    calcMode="spline"
                    keySplines="0.3 0 0.7 1;0.3 0 0.7 1"
                    values="0;1;0"
                    keyTimes="0;0.5;1"
                    dur="1s"
                    repeatCount="indefinite"
                />
            </circle>
        </g>
        <g transform="translate(60 50)">
            <circle
                cx={0}
                cy={0}
                r={7}
                fill="var(--f-color-highlight)"
                transform="scale(0.42525 0.42525)">
                <animateTransform
                    attributeName="transform"
                    type="scale"
                    begin="-0.125s"
                    calcMode="spline"
                    keySplines="0.3 0 0.7 1;0.3 0 0.7 1"
                    values="0;1;0"
                    keyTimes="0;0.5;1"
                    dur="1s"
                    repeatCount="indefinite"
                />
            </circle>
        </g>
        <g transform="translate(80 50)">
            <circle
                cx={0}
                cy={0}
                r={7}
                fill="var(--f-color-highlight)"
                transform="scale(0.113418 0.113418)">
                <animateTransform
                    attributeName="transform"
                    type="scale"
                    begin="0s"
                    calcMode="spline"
                    keySplines="0.3 0 0.7 1;0.3 0 0.7 1"
                    values="0;1;0"
                    keyTimes="0;0.5;1"
                    dur="1s"
                    repeatCount="indefinite"
                />
            </circle>
        </g>
    </svg>
)

export const DockToggle = () => {
    const [dock, setDock] = useLocalStorage('FRONTEER_DOCK', false)

    const handleChange = () => {
        const on = !dock
        dispatchCustomEvent('dock', on)
        setDock(on)
    }

    return (
        <Toggle
            on={dock}
            onChange={handleChange}
        />
    )
}

const AppMain = (props: any) => {
    const [pushNotificationsNotification, setPushNotificationsNotification] = useState(false)
    const [notice, setNotice] = useLocalStorage('FRONTEER_WELCOME_MESSAGE', true)
    const navigate = useNavigate()
    const { getToken, parseJwt, getUserId } = useAuth()
    const { getStorage, setStorage, deleteStorage } = useStorage()
    const { workspaceId } = useParams()
    const { connected } = useContext(WebsocketContext)
    const hideNav = useAppStore((state) => state.hideNav)
    const [celebrate, setCelebrate] = useState(false)
    const [okay, setOkay] = useState(getToken() != '')
    const [dock, setDock] = useLocalStorage('FRONTEER_DOCK', false)

    const onAppMessageReceived = (event) => {
        if (!event.data) return
        if (!event.data.type) return
        if (!event.data.content) return

        // SYNC_MESSAGE_HEIGHT -> message.component
        // APP_PANEL -> common (action)
        // APP_MODAL -> common (action)
        // EventService.getInstance().emit(event.data.type, event.data.content)
        // useFronteerEvent
    }

    const setupCordovaPushNotifications = async (userId) => {
        const token = getToken()

        if (IS_CORDOVA) {
            // When things are ready
            document.addEventListener(
                'deviceready',
                () => {
                    window['plugins']?.OneSignal.setLogLevel({ logLevel: 6, visualLevel: 0 })

                    let notificationOpenedCallback = function (jsonData) {
                        console.log('notificationOpenedCallback: ' + JSON.stringify(jsonData))
                    }
                    // Set your iOS Settings
                    let iosSettings = {}
                    iosSettings['kOSSettingsKeyAutoPrompt'] = false
                    iosSettings['kOSSettingsKeyInAppLaunchURL'] = false

                    window['plugins']?.OneSignal.startInit(ONESIGNAL_KEY)
                        .handleNotificationOpened(notificationOpenedCallback)
                        .iOSSettings(iosSettings)
                        .inFocusDisplaying(window['plugins']?.OneSignal.OSInFocusDisplayOption.Notification)
                        .endInit()

                    // The promptForPushNotificationsWithUserResponse function will show the iOS push notification prompt.
                    // We recommend removing the following code and instead using an In-App Message to prompt for notification permission (See step 6)
                    window['plugins']?.OneSignal.promptForPushNotificationsWithUserResponse(function (accepted) {
                        console.log('User accepted notifications: ' + accepted)
                    })

                    // Tag this user with his own id
                    window['plugins']?.OneSignal.sendTags({ userId })
                    window['plugins']?.OneSignal.getPermissionSubscriptionState(function (status) {
                        // Player ID = status.subscriptionStatus.userId
                        // Push token = status.subscriptionStatus.pushToken
                        // alert('Player ID: ' + status.subscriptionStatus.userId + '\npushToken = ' + status.subscriptionStatus.pushToken)
                        // Update their mobile push
                        subscribeUserMobile({ token, userId, pushToken: status.subscriptionStatus.userId })
                    })
                },
                false
            )
        }
    }

    const setupServiceWorker = async (userId) => {
        if ('serviceWorker' in navigator) {
            try {
                const register = await navigator.serviceWorker.register('/sw.js', {
                    scope: '/',
                })
                let serviceWorker

                if (register.installing) {
                    serviceWorker = register.installing
                } else if (register.waiting) {
                    // Service worker goes into waiting
                    // TODO: Maybe in future show button that sends message to sw
                    serviceWorker = register.waiting

                    // For now - force it
                    serviceWorker.postMessage('SKIP_WAITING')
                } else if (register.active) {
                    serviceWorker = register.active
                }

                if (serviceWorker) {
                    if (serviceWorker.state == 'activated') {
                        checkPushNotificationsAreEnabled()
                    }
                }

                // Triggered by the skipWaiting()
                serviceWorker.addEventListener('statechange', async (e) => {
                    try {
                        if (e.target.state == 'activated') {
                            checkPushNotificationsAreEnabled()
                        }
                    } catch (e) {
                        logger(e)
                    }
                })
            } catch (e) {
                logger(e)
            }
        } else {
            logger('Service workers are not supported in this browser')
        }
    }

    const checkPushNotificationsAreEnabled = async () => {
        const token = getToken()
        const { userId } = parseJwt(token)

        // For Safari on iOS (because they don't support PN)
        if (!navigator.permissions) return

        const { state } = await navigator.permissions.query({
            name: 'notifications',
        })

        if (state == 'granted') {
            setStorage('PN', 'YES')
            setPushNotificationsNotification(false)

            // Now get their device ID
            subscribeUser({ token, userId })
        } else if (state == 'denied') {
            setStorage('PN', 'NO')
            setPushNotificationsNotification(false)
        } else {
            deleteStorage('PN')
            setPushNotificationsNotification(true)
        }
    }

    const handlePushNotificationsSetup = async () => {
        const token = getToken()
        const { userId } = parseJwt(token)

        if ('PushManager' in window) {
            const permission = await askPushNotificationPermission()
            const cookie = getStorage('PN')

            // If they have granted us permission
            if (permission == 'granted') {
                setStorage('PN', 'YES')
                setPushNotificationsNotification(false)

                // Now get their device ID
                await subscribeUser({ token, userId })
            } else {
                setStorage('PN', 'NO')
                setPushNotificationsNotification(false)
            }
        }
    }

    const dismissPushNotifications = () => {
        setStorage('PN', 'NO')
        setPushNotificationsNotification(false)
    }

    const fetchData = async () => {
        try {
            // const { token } = await AuthService.currentAuthenticatedUser()
            // const { userId } = AuthService.parseJwt(token)
            // setUserId(userId)
            // dispatch(fetchUser(userId))
            // dispatch(initialize(userId))
            // setupServiceWorker(userId)
            // setupCordovaPushNotifications(userId)

            if (getToken() == '') {
                navigate('/auth')
            } else {
                setOkay(true)
            }
        } catch (e) {
            // navigate('/auth')
        }
    }

    const handleCelebrate = (e) => {
        setCelebrate(true)
        setTimeout(() => setCelebrate(false), 3000)
    }

    const handleDock = ({ detail }) => {
        setDock(detail)
    }

    useWindowEvent('message', onAppMessageReceived)
    useCustomEvent('celebrate', handleCelebrate)
    useCustomEvent('dock', handleDock)

    useEffect(() => {
        fetchData()
    }, [])

    if (!okay) return <SpinnerOverlay />

    return (
        <>
            {celebrate && (
                <div style={{ position: 'fixed', top: 0, left: 0, zIndex: 100000, height: 0, width: '100%' }}>
                    <ConfettiExplosion
                        width={1000}
                        duration={3000}
                        style={{ position: 'absolute', top: -90, left: '50%', zIndex: 100000 }}
                    />
                </div>
            )}

            <App bg="transparent">
                {notice && <Welcome onDismiss={() => setNotice(false)} />}
                {/*   
              {!connected && (
                    <Notification
                        height="2.5rem"
                        variant="highlight">
                        <NotificationContent
                            row
                            justifyContent="flex-start">
                            <Text fontWeight="bold">Connecting</Text>
                            <Loader />
                        </NotificationContent>
                    </Notification>
                )}
                */}

                {!connected && (
                    <LoadingLine
                        loading={true}
                        variant="highlight"
                    />
                )}
                {/* 
                <View column>
                    {[50,100,200,300,400,500,600,700,800,900,1000].map((color) => (
                        <View bg={`var(--ui-${color})`} width={250} height={50} border="1px solid black" />
                    ))}
                </View>
 */}
                {/* 
                <View
                    width="100%"
                    height={3}
                    bg="linear-gradient(45deg, var(--f-color-violet-600), var(--f-color-purple-600))"
                /> 
                */}

                <If if={pushNotificationsNotification}>
                    <Notification variant="highlight">
                        <NotificationContent>
                            <Text fontWeight="bold">Push notifications are disabled. Enable them now?</Text>
                        </NotificationContent>
                        <NotificationIcon onClick={handlePushNotificationsSetup}>
                            <Icon icon={FICheck} />
                        </NotificationIcon>
                        <NotificationIcon onClick={dismissPushNotifications}>
                            <Icon icon={FIX} />
                        </NotificationIcon>
                    </Notification>
                </If>

                {/* <ToolbarView /> */}
                {/* <Searchbar /> */}

                <Content
                    bg="transparent"
                    height={connected ? 'calc(100% - 0rem)' : 'calc(100% - 0rem)'}>
                    {(dock || hideNav) && <Dock />}

                    <div
                        style={{ flex: 1, height: '100%' }}
                        className="f-col">
                        <div
                            style={{ flex: 1, width: '100%', height: '100%' }}
                            className="f-row">
                            {workspaceId && <Nav />}

                            <Main column>
                                <View
                                    row
                                    flex={1}
                                    alignItems="flex-start"
                                    width="100%">
                                    <View
                                        row
                                        flex={1}
                                        position="relative"
                                        justifyContent="flex-start"
                                        height="100%">
                                        <Outlet />
                                    </View>
                                </View>
                            </Main>
                        </div>
                    </div>
                </Content>
            </App>
        </>
    )
}

export const AppLayout = (props) => {
    return (
        <WebsocketProvider url={WEBSOCKET_HOST}>
            <AppMain />
        </WebsocketProvider>
    )
}
